import { Ref, forwardRef } from "react"
import { Form, FormikHelpers, FormikProps } from "formik"
import { Formik } from "../../../../../../shared/components/forms/SchemaFormik"
import {
    BankAccountDetails,
    CreateUpdateMerchant,
    CreateUpdateMerchantFormValues,
    CurrencyCode,
    SubscriptionDetailsInfo,
} from "../supplierMerchantsTypes"
import { CreateUpdateMerchantFormContent } from "./CreateUpdateMerchantFormContent"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { UserType } from "../../../../../login/loginTypes"
import { Agreement, WellknownAgreementType } from "../../../settings/posAcquirer/SupplierAdminPOSTypes"
import {
    FetchAcquirerAgreementsResponse,
    fetchAcquirerAgreementsForSupplier,
    POSQuery,
} from "../../../settings/posAcquirer/SupplierAdminPOSApi"
import { useCurrentSupplier } from "../../../../../suppliers/supplierHooks"
import { defaultMerchantInitialValues } from "../../../../../merchant/admin/account/merchantAccount/constants"
import { useMerchantValidationSchema } from "../../../../../merchant/admin/account/merchantAccount/hooks/useMerchantValidationSchema"
import { EntityHistory } from "../../../../../support/admin/entities/supportEntitiesTypes"

type CreateUpdateMerchantFormProps = Readonly<{
    initialValues?: CreateUpdateMerchantFormValues | null
    bankAccountDetails?: BankAccountDetails
    onSubmit: (formData: { merchant: CreateUpdateMerchant; supplierId: number }) => void
    history?: EntityHistory
    subscriptionDetails?: SubscriptionDetailsInfo
}>
type FormikRef = Ref<FormikProps<CreateUpdateMerchantFormValues>>

const selectSupplierAgreements = (data: FetchAcquirerAgreementsResponse) => data?.agreements || []

export const CreateUpdateMerchantForm = forwardRef(
    (
        { initialValues, bankAccountDetails, onSubmit, history, subscriptionDetails }: CreateUpdateMerchantFormProps,
        ref: FormikRef,
    ) => {
        const validationSchema = useMerchantValidationSchema()
        const { supplierId } = useCurrentSupplier()

        const supplierAgreementsResult = useApiQuery(fetchAcquirerAgreementsForSupplier, {
            queryName: POSQuery.AGREEMENTS_DETAILS,
            dependencies: {
                supplierId,
            },
            select: selectSupplierAgreements,
        })

        const isAdyenPosAgreement = (agreement: Agreement) => agreement.type === WellknownAgreementType.ADYEN_POS

        const hasAdyenPosAgreement = supplierAgreementsResult.data
            ? supplierAgreementsResult.data.some(isAdyenPosAgreement)
            : false

        function handleSubmit(
            values: CreateUpdateMerchantFormValues,
            _helpers: FormikHelpers<CreateUpdateMerchantFormValues>,
        ) {
            const isEmptyAcquiringDetails = Object.keys(values.acquiringDetails).length === 0

            const acquiringInfo = !isEmptyAcquiringDetails && {
                acquiring: {
                    ...values.acquiringDetails.acquiring,
                    type: values?.acquiringDetails?.acquiring?.type || "",
                    defaultCurrencyCode: values?.acquiringDetails?.acquiring?.defaultCurrencyCode || CurrencyCode.DKK,
                    industryCodes: values?.acquiringDetails?.acquiring?.industryCodes.map((el) => ({ refId: el })),
                    salesChannels: values?.acquiringDetails?.acquiring?.salesChannels
                        ? [values.acquiringDetails.acquiring.salesChannels]
                        : [],
                },
            }
            const areContactDetailsEmpty = Object.values(values.contactDetails).every((value) => value === "")

            const modifyData = {
                type: UserType.MERCHANT,
                ...(!!values.caseNumber && { caseNumber: values.caseNumber }),
                name: values.name,
                address: values.address,
                ...(!areContactDetailsEmpty && { contactDetails: values.contactDetails }),
                isActive: values.isActive,
                openingHours: values.openingHours,
                ...(!isEmptyAcquiringDetails && hasAdyenPosAgreement
                    ? {
                          acquiringDetails: {
                              ...acquiringInfo,
                          },
                      }
                    : {}),
            }

            onSubmit({ merchant: modifyData, supplierId: supplierId ?? 0 })
        }

        return (
            <>
                <Formik<CreateUpdateMerchantFormValues>
                    // enableReinitialize
                    initialValues={initialValues ?? defaultMerchantInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={ref}
                >
                    <Form>
                        <CreateUpdateMerchantFormContent
                            isShowPayments={hasAdyenPosAgreement}
                            paymentList={supplierAgreementsResult.data || []}
                            bankAccountDetails={bankAccountDetails}
                            history={history}
                            subscriptionDetails={subscriptionDetails}
                        />
                    </Form>
                </Formik>
            </>
        )
    },
)
